import { PrintDocument, RenderOutput } from '../document'
import { asciiText, mmToDots } from '../text'
import { layoutLabel } from './labelLayout'

const q = (text: string) => asciiText(text).replace(/"/g, "'")

const labelBlocks = (doc: PrintDocument) => {
  if (doc.kind === 'sheet') throw new Error('A label printer prints one label at a time; use a label document')
  return { blocks: doc.blocks, copies: doc.kind === 'label' ? (doc.copies ?? 1) : 1 }
}

/** TSPL / TSPL2 (TSC, Xprinter XP-3xx / XP-4xx label models, Gprinter, most low-cost label printers). */
export const renderTspl = (doc: PrintDocument, p: Record<string, any>): RenderOutput => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const { blocks, copies } = labelBlocks(doc)
  const l = layoutLabel(blocks, p)
  const out = [
    `SIZE ${p.label_width_mm} mm,${p.label_height_mm} mm`,
    `GAP ${p.gap_mm} mm,0 mm`,
    `SPEED ${p.speed}`,
    `DENSITY ${p.density}`,
    'DIRECTION 1',
    'CLS',
  ]
  for (const it of l.items) {
    if (it.kind === 'text') out.push(`TEXT ${it.x},${it.y},"2",0,${it.scale},${it.scale},"${q(it.text)}"`)
    if (it.kind === 'line') out.push(`BAR ${it.x},${it.y},${it.width},2`)
    if (it.kind === 'barcode') {
      const type = it.symbology === 'ean13' ? 'EAN13' : '128'
      const value = it.symbology === 'ean13' ? it.value.slice(0, 12) : it.value
      out.push(`BARCODE ${it.x},${it.y},"${type}",${it.height},${it.showText ? 2 : 0},0,${it.narrow},${it.narrow * 2},"${q(value)}"`)
    }
    if (it.kind === 'qr') out.push(`QRCODE ${it.x},${it.y},M,${it.cell},A,0,"${q(it.value)}"`)
  }
  out.push(`PRINT ${copies},1`)
  return { text: out.join('\r\n') + '\r\n', mime: 'text/plain' }
}

/** ZPL II (Zebra, and printers emulating it). */
export const renderZpl = (doc: PrintDocument, p: Record<string, any>): RenderOutput => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const { blocks, copies } = labelBlocks(doc)
  const l = layoutLabel(blocks, p)
  const k = Number(p.dpi) / 203
  const out = ['^XA', '^CI0', `^PW${l.width}`, `^LL${l.height}`]
  for (const it of l.items) {
    if (it.kind === 'text') {
      const h = Math.round(20 * k * it.scale)
      out.push(`^FO${it.x},${it.y}^A0N,${h},${Math.round(h * 0.6)}^FD${asciiText(it.text).replace(/[\^~]/g, ' ')}^FS`)
    }
    if (it.kind === 'line') out.push(`^FO${it.x},${it.y}^GB${it.width},2,2^FS`)
    if (it.kind === 'barcode') {
      const cmd = it.symbology === 'ean13' ? `^BEN,${it.height},${it.showText ? 'Y' : 'N'},N` : `^BCN,${it.height},${it.showText ? 'Y' : 'N'},N,N`
      const value = it.symbology === 'ean13' ? it.value.slice(0, 12) : it.value
      out.push(`^FO${it.x},${it.y}^BY${it.narrow}${cmd}^FD${asciiText(value)}^FS`)
    }
    if (it.kind === 'qr') out.push(`^FO${it.x},${it.y}^BQN,2,${it.cell}^FDMA,${asciiText(it.value)}^FS`)
  }
  out.push(`^PQ${copies}`, '^XZ')
  return { text: out.join('\n') + '\n', mime: 'text/plain' }
}

/** EPL2 (older Zebra / Eltron desktop label printers). QR is not in EPL2; its text prints instead. */
export const renderEpl = (doc: PrintDocument, p: Record<string, any>): RenderOutput => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const { blocks, copies } = labelBlocks(doc)
  const l = layoutLabel(blocks, p)
  const out = ['', 'N', `q${l.width}`, `Q${l.height},${mmToDots(Number(p.gap_mm), Number(p.dpi))}`]
  for (const it of l.items) {
    if (it.kind === 'text') out.push(`A${it.x},${it.y},0,2,${it.scale},${it.scale},N,"${q(it.text)}"`)
    if (it.kind === 'line') out.push(`LO${it.x},${it.y},${it.width},2`)
    if (it.kind === 'barcode') {
      const type = it.symbology === 'ean13' ? 'E30' : '1'
      const value = it.symbology === 'ean13' ? it.value.slice(0, 12) : it.value
      out.push(`B${it.x},${it.y},0,${type},${it.narrow},${it.narrow * 2},${it.height},${it.showText ? 'B' : 'N'},"${q(value)}"`)
    }
    if (it.kind === 'qr') out.push(`A${it.x},${it.y},0,2,1,1,N,"${q(it.value)}"`)
  }
  out.push(`P${copies}`)
  return { text: out.join('\n') + '\n', mime: 'text/plain' }
}
